import { Component, Inject, Injector, OnInit } from "@angular/core";
import { AbstractControl, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { KeycloakService } from "keycloak-angular";
import { take } from "rxjs/operators";
import { GenericComponent } from "../../components/generic.component";
import { BudgetPartItemsDTO } from "../../models/budget-part-items-dto";
import { RoleEnum } from "../../enums/roles.enum";
import { ValidatorsService } from "@shared/validators/validators.service";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";

interface PropsType {
  isBoschClient: boolean;
}

@Component({
    selector: "app-smartfleet-budget-service-item-form-modal",
    templateUrl: "./budget-service-item-form-modal.component.html",
    styleUrls: ["./budget-service-item-form-modal.component.scss"],
    standalone: false
})
export class BudgetServiceItemFormModalComponent
  extends GenericComponent
  implements OnInit
{
  labelServiceCarWorkshopPrice: string;
  labelServiceClientPrice: string;

  //hora
  hours = [
    { time: "00", value: 0 },
    { time: "01", value: 1 },
    { time: "02", value: 2 },
    { time: "03", value: 3 },
    { time: "04", value: 4 },
    { time: "05", value: 5 },
    { time: "06", value: 6 },
    { time: "07", value: 7 },
    { time: "08", value: 8 },
    { time: "09", value: 9 },
    { time: "10", value: 10 },
    { time: "11", value: 11 },
    { time: "12", value: 12 },
    { time: "13", value: 13 },
    { time: "14", value: 14 },
    { time: "15", value: 15 },
    { time: "16", value: 16 },
    { time: "17", value: 17 },
    { time: "18", value: 18 },
    { time: "19", value: 19 },
    { time: "20", value: 20 },
    { time: "21", value: 21 },
    { time: "22", value: 22 },
    { time: "23", value: 23 }
  ];
  minutes = [
    { time: "00", value: 0 },
    { time: "15", value: 0.25 },
    { time: "30", value: 0.5 },
    { time: "45", value: 0.75 }
  ];
  selectedHours = 0;
  selectedHoursService = 0;
  selectedMinutes = 0;
  selectedMinutesService = 0;
  totalTime = 0;
  totalTimeService = 0;
  timeString = "";

  form = this.fb.group({
    customService: [true],
    serviceName: ["", [Validators.required]],
    carWorkshopPrice: ["", [Validators.required]],
    clientPrice: ["", [Validators.required]],
    amountOfHours: [""],
    selectedHoursService: [""],
    selectedMinutesService: [""],
    timeString: [""],
    courtesy: [false, [Validators.required]],
    guarantee: [false, [Validators.required]],
  });

  budgetPartItems: BudgetPartItemsDTO;
  constructor(
    private keycloakService: KeycloakService,
    injector: Injector,
    @Inject(MAT_DIALOG_DATA) private data: PropsType,
    private matDialogRef: MatDialogRef<BudgetServiceItemFormModalComponent>,
    private validatorsService: ValidatorsService
  ) {
    super(injector);
  }

  ngOnInit() {
    this.buildPage();

    if (this.data.isBoschClient || this.isCarWorkshopLogged()) {
      this.form.get("clientPrice").disable();
    }

    this.setupToggleListeners();
  }

  buildPage(): void {
    const strings = [
      "input-price",
      "input-price-workshop",
      "input-price-client"
    ];

    this.translateService
      .get(strings)
      .pipe(take(1))
      .subscribe((message) => {
        if (
          this.isBackofficeOperationLogged() ||
          this.isBackofficeExternalLogged() ||
          this.isBackofficeApproverManagerLogged() ||
          this.isBackofficeAdministratorLogged()
        ) {
          this.labelServiceCarWorkshopPrice = message["input-price-workshop"];
          this.labelServiceClientPrice = message["input-price-client"];
        } else {
          this.labelServiceCarWorkshopPrice = message["input-price"];
          this.labelServiceClientPrice = message["input-price"];
        }
      });
  }

  closeDialog(isSubmitted: boolean): void {
    if (isSubmitted) {
      if (this.form.valid) {
        if (!this.data.isBoschClient && this.isCarWorkshopLogged()) {
          this.form.get("clientPrice").clearValidators();
          this.form.get("clientPrice").setValue("0,00");
          this.form.get("clientPrice").updateValueAndValidity();
        }
        this.matDialogRef.close(this.form.getRawValue());
      }
    } else {
      this.matDialogRef.close(null);
    }
  }

  get getFormControls() {
    return this.form.controls;
  }

  isBackofficeOperationLogged() {
    return this.keycloakService
      .getUserRoles()
      .includes(RoleEnum.BACKOFFICE_OPERATION);
  }

  isBackofficeExternalLogged() {
    return this.keycloakService
      .getUserRoles()
      .includes(RoleEnum.BACKOFFICE_EXTERNAL);
  }

  isBackofficeApproverManagerLogged() {
    return this.keycloakService
      .getUserRoles()
      .includes(RoleEnum.BACKOFFICE_APPROVER_MANAGER);
  }

  isBackofficeAdministratorLogged() {
    return this.keycloakService
      .getUserRoles()
      .includes(RoleEnum.BACKOFFICE_ADMINISTRATOR);
  }

  isCarWorkshopLogged() {
    return this.keycloakService.getUserRoles().includes(RoleEnum.CAR_WORKSHOP);
  }

  handleServiceCarWorkshopPrice() {
    if (this.data.isBoschClient) {
      this.form
        .get("clientPrice")
        .setValue(this.form.get("carWorkshopPrice").value);
    }

    this.handleServiceClientPrice();
  }

  handleServiceClientPrice() {
    this.form
      .get("clientPrice")
      .setValidators(
        this.validatorsService.biggerOrEqualValidator(
          this.form.get("carWorkshopPrice").value,
          this.form.get("clientPrice").value
        )
      );
    this.form.get("clientPrice").updateValueAndValidity();
    this.form.markAllAsTouched();
  }

  setHours(e) {
    this.selectedHours = e.value == "0" ? 0 : e.value;
    this.setTime();
  }

  setMinutes(e) {
    this.selectedMinutes = e.value == "0" ? 0 : e.value;
    this.setTime();
  }

  setTime() {
    this.timeString = "";
    this.totalTime = this.selectedHours + this.selectedMinutes;
    this.timeString = this.getTimeString(
      this.selectedHours,
      this.selectedMinutes
    );
    this.form.get("amountOfHours").setValue(this.totalTime);
    this.form.get("timeString").setValue(this.timeString);
    if (this.totalTime == 0) {
      this.form.get("amountOfHours").setValue("");
      this.form.get("timeString").setValue("");
    }
  }

  getTimeString(hours, minutes) {
    let minutesConverted = "";
    let timeString = hours != 0 ? hours + "h" : "";
    switch (minutes) {
      case 0:
        minutesConverted = "00";
        break;
      case 0.25:
        minutesConverted = "15";
        break;
      case 0.5:
        minutesConverted = "30";
        break;
      case 0.75:
        minutesConverted = "45";
        break;
      default:
        minutesConverted = "00";
        break;
    }
    return (timeString += minutes != 0 ? minutesConverted + "min" : "");
  }

  getThousandSeparator() {
    return this.maskService.getThousandSeparator();
  }

  private setupToggleListeners(): void {
    this.onGuaranteeChange();
    this.onCourtesyChange();
  }

  private onGuaranteeChange(): void {
    this.form.get('guarantee')?.valueChanges.subscribe((value: boolean) => {
      const courtesyControl = this.form.get('courtesy');
      if (value) {
        this.disableAndUncheck(courtesyControl);
      } else {
        courtesyControl?.enable({ emitEvent: false });
      }
    });
  }

  private onCourtesyChange(): void {
    this.form.get('courtesy')?.valueChanges.subscribe((value: boolean) => {
      const guaranteeControl = this.form.get('guarantee');
      if (value) {
        this.disableAndUncheck(guaranteeControl);
      } else {
        guaranteeControl?.enable({ emitEvent: false });
      }
    });
  }

  private disableAndUncheck(control: AbstractControl | null): void {
    control?.setValue(false, { emitEvent: false });
    control?.disable({ emitEvent: false });
  }

  handleGuaranteeAndCourtesy(event: MatSlideToggleChange): void {
    const { checked } = event;

    const fields = [
      'carWorkshopPrice',
      'clientPrice'
    ];

    fields.forEach(field => {
      const control = this.form.get(field);
      if (!control) return;

      if (checked) {
        control.setValue("0,00", { emitEvent: false });
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }
}
